import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import CheckBoxOutlinedIcon from "@mui/icons-material/CheckBoxOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import QueryBuilderOutlinedIcon from "@mui/icons-material/QueryBuilderOutlined";
import RadioButtonCheckedOutlinedIcon from "@mui/icons-material/RadioButtonCheckedOutlined";
import ShortTextOutlinedIcon from "@mui/icons-material/ShortTextOutlined";
import SubjectOutlinedIcon from "@mui/icons-material/SubjectOutlined";
import type React from "react";
import NumberIcon from "../../components/NumberIcon";

export const FieldTypes: { [fieldName: string]: symbol } = {
  choice: Symbol.for("choice"),
  date: Symbol.for("date"),
  number: Symbol.for("number"),
  radio: Symbol.for("radio"),
  plain_text: Symbol.for("plain text"),
  formatted_text: Symbol.for("formatted text"),
  time: Symbol.for("time"),
  uri: Symbol.for("uri"),
  reference: Symbol.for("reference"),
  attachment: Symbol.for("attachment"),
  link: Symbol.for("link"),
};

export type FieldType = (typeof FieldTypes)[keyof typeof FieldTypes];

export const compatibleFieldTypes = (fieldType: FieldType): Array<FieldType> => {
  const types = new Set([fieldType]);
  if (fieldType !== FieldTypes.reference && fieldType !== FieldTypes.attachment) {
    types.add(FieldTypes.formatted_text);
    types.add(FieldTypes.plain_text);
    types.add(FieldTypes.radio);
    types.add(FieldTypes.choice);
    // detection only types a column as link when every present value parses, so a column that
    // lost the type for any other reason (a value over the non-text length limit, say) has no
    // other route back to it. Choosing link for values that do not parse is refused at import
    // with errors.inventory.import.linkValueInvalid, before anything is saved.
    types.add(FieldTypes.link);
  }
  return [...types];
};

export const fieldTypeToApiString = (fieldType: FieldType): string => {
  if (!(typeof fieldType === "symbol")) throw new Error("FieldTypes are all Symbols.");
  const map = {
    [FieldTypes.choice]: "choice",
    [FieldTypes.date]: "date",
    [FieldTypes.number]: "number",
    [FieldTypes.radio]: "radio",
    [FieldTypes.plain_text]: "string",
    [FieldTypes.formatted_text]: "text",
    [FieldTypes.uri]: "uri",
    [FieldTypes.time]: "time",
    [FieldTypes.reference]: "reference",
    [FieldTypes.attachment]: "attachment",
    [FieldTypes.link]: "link",
  };
  if (!map[fieldType]) throw new Error(`Invalid field type: Symbol(${Symbol.keyFor(fieldType)})`);
  return map[fieldType];
};

export const apiStringToFieldType = (apiString: string): FieldType => {
  const map: { [fieldName: string]: symbol } = {
    choice: FieldTypes.choice,
    date: FieldTypes.date,
    number: FieldTypes.number,
    radio: FieldTypes.radio,
    string: FieldTypes.plain_text,
    text: FieldTypes.formatted_text,
    uri: FieldTypes.uri,
    time: FieldTypes.time,
    reference: FieldTypes.reference,
    attachment: FieldTypes.attachment,
    link: FieldTypes.link,
  };
  if (!map[apiString]) throw new Error(`Invalid api string '${apiString}'; not a valid field type.`);
  return map[apiString];
};

export const hasOptions = (fieldType: FieldType): boolean =>
  fieldType === FieldTypes.choice || fieldType === FieldTypes.radio;

export const FIELD_LABEL: { [fieldName: FieldType]: string } = {
  [FieldTypes.choice]: "Choice",
  [FieldTypes.date]: "Date",
  [FieldTypes.number]: "Number",
  [FieldTypes.radio]: "Radio",
  [FieldTypes.plain_text]: "Plain text",
  [FieldTypes.formatted_text]: "Formatted text",
  [FieldTypes.uri]: "URI",
  [FieldTypes.time]: "Time",
  [FieldTypes.attachment]: "Attachment",
  [FieldTypes.reference]: "Reference",
  [FieldTypes.link]: "Link",
};

export const FIELD_ICON: { [fieldName: FieldType]: React.ReactNode } = {
  [FieldTypes.choice]: <CheckBoxOutlinedIcon />,
  [FieldTypes.date]: <EventOutlinedIcon />,
  [FieldTypes.number]: <NumberIcon />,
  [FieldTypes.radio]: <RadioButtonCheckedOutlinedIcon />,
  [FieldTypes.plain_text]: <ShortTextOutlinedIcon />,
  [FieldTypes.formatted_text]: <SubjectOutlinedIcon />,
  [FieldTypes.uri]: <LinkOutlinedIcon />,
  [FieldTypes.time]: <QueryBuilderOutlinedIcon />,
  [FieldTypes.attachment]: <AttachFileOutlinedIcon />,
  [FieldTypes.link]: <LinkOutlinedIcon />,
};

export const FIELD_HELP_TEXT: { [fieldName: FieldType]: string } = {
  [FieldTypes.choice]: "Choose many options.",
  [FieldTypes.date]: "A single date.",
  [FieldTypes.number]: "Any numerical value.",
  [FieldTypes.radio]: "Choose one option.",
  [FieldTypes.plain_text]: "A short plain-text label.",
  [FieldTypes.formatted_text]: "A longer formatted piece of text.",
  [FieldTypes.uri]: "A Web link.",
  [FieldTypes.time]: "A time of day.",
  [FieldTypes.attachment]: "A single file, e.g. document, or chemistry file.",
  [FieldTypes.reference]: "",
  [FieldTypes.link]: "A link to another Inventory item or ELN record.",
};

export const SUPPORTED_TYPES: Set<FieldType> = new Set<FieldType>([
  FieldTypes.choice,
  FieldTypes.date,
  FieldTypes.number,
  FieldTypes.radio,
  FieldTypes.plain_text,
  FieldTypes.formatted_text,
  FieldTypes.uri,
  FieldTypes.time,
  FieldTypes.attachment,
  FieldTypes.link,
]);

export const FIELD_DATA: {
  [fieldName: FieldType]: {
    icon: React.ReactNode;
    help: string;
    label: string;
  };
} = Object.fromEntries(
  Object.values(FieldTypes).map((f) => [f, { icon: FIELD_ICON[f], help: FIELD_HELP_TEXT[f], label: FIELD_LABEL[f] }]),
);
